import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import TextField from '@mui/material/TextField'
import match from 'autosuggest-highlight/match'
import parseHighlight from 'autosuggest-highlight/parse'
import { useTranslate } from 'ra-core'
import { useEffect, useMemo, useState } from 'react'
import {
  useGetRecordRepresentation,
  useInfiniteGetList,
  useInput,
  InputHelperText,
  isRequired,
  useReference,
  useResourceContext,
  useSaveContext,
} from 'react-admin'

import { SYSTEM_PERMISSION_READ } from '../config/permissions'
import { useResourcePermissions } from '../domain/permissions'
import { useHelperText } from '../resources/common/forms'

const getNestedValue = (record, path) =>
  String(path)
    .split('.')
    .reduce((value, key) => value?.[key], record)

const getRecordLabel = (record, { optionText, recordRepresentation, translate, translateChoice }) => {
  if (!record) return ''

  let label

  if (typeof optionText === 'function') {
    label = optionText(record)
  } else if (typeof optionText === 'string') {
    label = getNestedValue(record, optionText)
  } else if (recordRepresentation) {
    label = recordRepresentation(record)
  } else {
    label = record?.designation ?? record?.name ?? record?.title ?? record?.label ?? record?.code ?? record?.id
  }

  if (translateChoice && typeof label === 'string') {
    return translate(label, { _: label })
  }

  return String(label ?? '')
}

const AdvancedReferenceInput = ({
  emptyText,
  emptyValue,
  excludedIds = [],
  disableAutoSelect = false,
  filter,
  filterToQuery = (searchText) => ({ search_designation: searchText }),
  format,
  parse,
  perPage = 10,
  readOnly,
  reference,
  sort,
  TextFieldProps = {},
  placeholderContent = null,
  translateChoice,
  optionText,
  validate,
  ...props
}) => {
  const translate = useTranslate()
  const isFormTypeFilter = !useSaveContext().mutationMode
  const [hasReadForReference] = useResourcePermissions(reference, SYSTEM_PERMISSION_READ)
  const helperText = useHelperText(props.source)
  const recordRepresentation = useGetRecordRepresentation(reference)
  const {
    field: { value, onChange },
    fieldState: { error, isTouched },
    formState: { isSubmitted },
  } = useInput({ ...props, format, parse, validate })

  const [open, setOpen] = useState(false)
  const [hasOpenedOnce, setHasOpenedOnce] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [debouncedInputValue, setDebouncedInputValue] = useState('')
  const isFieldRequired = isRequired(validate)
  const shouldLoadInitially = isFieldRequired || isFormTypeFilter

  const normalizedFilter = useMemo(
    () => ({ excluded_ids: excludedIds, ...filter, ...(open ? filterToQuery(debouncedInputValue) : {}) }),
    [debouncedInputValue, excludedIds, filter, filterToQuery, open],
  )

  const { data, total, isPending, isFetchingNextPage, fetchNextPage, hasNextPage } = useInfiniteGetList(
    reference,
    {
      pagination: { page: 1, perPage },
      sort,
      filter: normalizedFilter,
    },
    { enabled: hasReadForReference && (hasOpenedOnce || shouldLoadInitially) },
  )

  const { referenceRecord: selectedRecord } = useReference({
    reference,
    id: value,
    options: { enabled: Boolean(value) },
  })

  const fetchedOptions = useMemo(() => data?.pages?.flatMap((page) => page.data) ?? [], [data])
  const shouldAutoSelectSingleOption = !disableAutoSelect && shouldLoadInitially && !hasOpenedOnce && total === 1
  const isLoading = (hasOpenedOnce || shouldLoadInitially) && (isPending || isFetchingNextPage)
  const effectiveEmptyText = emptyText ?? (isFormTypeFilter ? 'mymove.all' : null)
  const emptyOption = useMemo(
    () =>
      effectiveEmptyText == null
        ? null
        : {
            id: emptyValue ?? '',
            label: translate(effectiveEmptyText, { _: effectiveEmptyText }),
          },
    [effectiveEmptyText, emptyValue, translate],
  )

  const options = useMemo(() => {
    const merged = new Map()
    ;[emptyOption, selectedRecord, ...fetchedOptions].filter(Boolean).forEach((record) => {
      merged.set(record.id, record)
    })
    return [...merged.values()]
  }, [emptyOption, fetchedOptions, selectedRecord])

  const selectedOption = useMemo(
    () => (value ? options.find((option) => option.id === value) || selectedRecord : null),
    [options, selectedRecord, value],
  )

  const selectedLabel = useMemo(
    () => getRecordLabel(selectedOption, { optionText, recordRepresentation, translate, translateChoice }),
    [optionText, recordRepresentation, selectedOption, translate, translateChoice],
  )

  useEffect(() => {
    const onlyOption = fetchedOptions[0]
    const hasNoValue = value === undefined || value === null || value === ''
    const shouldSetOnlyOption = !isPending && shouldAutoSelectSingleOption && hasNoValue && onlyOption
    if (shouldSetOnlyOption) {
      onChange(onlyOption.id)
    }
  }, [fetchedOptions, isPending, onChange, shouldAutoSelectSingleOption, value])

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedInputValue(inputValue)
    }, 220)

    return () => clearTimeout(timeout)
  }, [inputValue])

  useEffect(() => {
    if (!open) {
      setInputValue(selectedLabel)
    }
  }, [open, selectedLabel])

  const isDisabled = readOnly || shouldAutoSelectSingleOption
  const resource = useResourceContext(props)
  const label = props.label === false ? false : (props.label ?? `resources.${resource}.fields.${props.source}`)
  const translatedLabel = typeof label === 'string' ? translate(label, { _: label }) : label
  const getOptionLabel = (record) =>
    record === emptyOption
      ? record.label
      : getRecordLabel(record, { optionText, recordRepresentation, translate, translateChoice })

  const handleChange = (_event, newValue) => {
    onChange(newValue?.id ?? emptyValue ?? '')
    setInputValue(getRecordLabel(newValue, { optionText, recordRepresentation, translate, translateChoice }))
  }

  const handleInputChange = (_event, newInputValue, reason) => {
    if (reason === 'reset' && selectedLabel) {
      setInputValue(selectedLabel)
      return
    }

    if (reason === 'clear') {
      setInputValue('')
      setDebouncedInputValue('')
      return
    }

    setInputValue(newInputValue)
  }

  const handleScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 48

    if (isNearBottom && hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }

  return (
    <Autocomplete
      {...props}
      className={`ra-input ra-input-${props.source}${props.className ? ` ${props.className}` : ''}`}
      disabled={isDisabled}
      open={open}
      onOpen={() => {
        setOpen(true)
        setHasOpenedOnce(true)
      }}
      onClose={() => {
        setOpen(false)
        setInputValue(selectedLabel)
      }}
      autoHighlight
      selectOnFocus
      clearOnBlur
      includeInputInList
      handleHomeEndKeys
      loading={isLoading}
      noOptionsText={translate('mymove.noOption', { _: 'No options' })}
      options={options}
      value={selectedOption}
      inputValue={inputValue}
      onChange={handleChange}
      onInputChange={handleInputChange}
      isOptionEqualToValue={(option, currentValue) => option?.id === currentValue?.id}
      getOptionLabel={getOptionLabel}
      filterOptions={(items) => items}
      ListboxProps={{ onScroll: handleScroll, role: 'listbox' }}
      renderOption={(optionProps, option) => {
        const label = getOptionLabel(option)
        const matches = inputValue ? match(label, inputValue, { insideWords: true }) : []
        const parts = parseHighlight(
          label,
          matches.map((m) => [m[0], m[1]]),
        )

        return (
          <li {...optionProps}>
            <span>
              {parts.map((part, index) => (
                <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                  {part.text}
                </span>
              ))}
            </span>
          </li>
        )
      }}
      renderInput={(params) => (
        <Box sx={{ position: 'relative', width: '100%' }}>
          <TextField
            {...params}
            label={translatedLabel}
            required={Boolean(validate)}
            error={Boolean(error) && (isTouched || isSubmitted)}
            helperText={
              <InputHelperText touched={isTouched || isSubmitted} error={error?.message} helperText={helperText} />
            }
            placeholder={undefined}
            InputProps={{
              ...params.InputProps,
              ...TextFieldProps.InputProps,
              startAdornment: TextFieldProps.InputProps?.startAdornment ?? params.InputProps.startAdornment,
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            inputProps={{
              ...params.inputProps,
              ...TextFieldProps.inputProps,
            }}
            sx={TextFieldProps.sx}
          />
          {placeholderContent && !selectedOption && !inputValue ? (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                pointerEvents: 'none',
                pl: 1.75,
                color: 'text.disabled',
                zIndex: 1,
              }}
            >
              {placeholderContent}
            </Box>
          ) : null}
        </Box>
      )}
    />
  )
}

export default AdvancedReferenceInput
